<template>
  <v-dialog
    v-model="open"
    fullscreen
    hide-overlay
    transition="dialog-bottom-transition"
  >
    <v-card class="menu-hub" flat>
      <div class="menu-hub__top">
        <div class="menu-hub__header" dir="rtl">
          <button
            type="button"
            class="menu-hub__icon-btn"
            :aria-label="$t('search-in-menu')"
            @click="openSearch"
          >
            <v-icon size="22" color="#455A64">mdi-magnify</v-icon>
          </button>
          <div class="menu-hub__header-title">
            {{ activeCategory ? activeCategory.title : hubTitle }}
          </div>
          <button
            type="button"
            class="menu-hub__icon-btn"
            :aria-label="$t('back')"
            @click="onBack"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <!-- fixed action strip; only menu body scrolls -->
        <div
          v-show="!activeCategory"
          class="menu-hub__actions"
          dir="rtl"
        >
          <div
            class="menu-hub__action menu-hub__action--file"
            role="button"
            tabindex="0"
            @click.capture.prevent="triggerAction('fileProcess')"
            @keydown.enter.prevent="triggerAction('fileProcess')"
          >
            <div class="menu-hub__action-icon">
              <my-icon
                name="file-upload-icon"
                fill="#000"
                :width="18"
                :height="18"
                :cursor-pointer="true"
              />
            </div>
            <span class="menu-hub__action-label">{{ $t("file") }}</span>
          </div>

          <div
            class="menu-hub__action menu-hub__action--history"
            role="button"
            tabindex="0"
            @click.capture.prevent="triggerAction('history')"
            @keydown.enter.prevent="triggerAction('history')"
          >
            <div class="menu-hub__action-icon">
              <UserHistory ref="history" />
            </div>
            <span class="menu-hub__action-label">{{ $t("history") }}</span>
          </div>

          <div
            class="menu-hub__action menu-hub__action--help"
            role="button"
            tabindex="0"
            @click.capture.prevent="triggerAction('help')"
            @keydown.enter.prevent="triggerAction('help')"
          >
            <div class="menu-hub__action-icon">
              <UserHelp ref="help" />
            </div>
            <span class="menu-hub__action-label">{{ $t("learn") }}</span>
          </div>

          <div
            class="menu-hub__action menu-hub__action--support"
            role="button"
            tabindex="0"
            @click.capture.prevent="triggerAction('support')"
            @keydown.enter.prevent="triggerAction('support')"
          >
            <div class="menu-hub__action-icon">
              <UserSupport ref="support" :touchable="true" />
            </div>
            <span class="menu-hub__action-label">{{ $t("support") }}</span>
          </div>
        </div>
      </div>

      <div class="menu-hub__body" dir="rtl">
        <!-- category children -->
        <div v-if="activeCategory" class="menu-hub__grid">
          <button
            v-for="(child, index) in visibleChildren"
            :key="'child-' + index"
            type="button"
            class="menu-hub__tile"
            @click="goToRoute(child)"
          >
            <v-icon size="26" color="#455A64">
              {{ child.icon || activeCategory.icon || "mdi-circle-outline" }}
            </v-icon>
            <span class="menu-hub__tile-label">{{ child.title }}</span>
          </button>
        </div>

        <!-- root categories -->
        <div v-else class="menu-hub__grid">
          <button
            v-for="(item, index) in visibleCategories"
            :key="'cat-' + index"
            type="button"
            class="menu-hub__tile"
            @click="openCategory(item)"
          >
            <v-icon size="28" color="#455A64">
              {{ item.icon || "mdi-view-grid-outline" }}
            </v-icon>
            <span class="menu-hub__tile-label">{{ item.title }}</span>
          </button>
        </div>
      </div>

      <!-- full-page menu search (invoice entity-picker style) -->
      <v-dialog
        v-model="searchOpen"
        fullscreen
        hide-overlay
        transition="fade-transition"
      >
        <v-card class="menu-search" flat>
          <header class="menu-search__header" dir="rtl">
            <span class="menu-search__header-spacer" />
            <h2 class="menu-search__title">{{ $t("search-in-menu") }}</h2>
            <button
              type="button"
              class="menu-hub__icon-btn"
              :aria-label="$t('back')"
              @click="closeSearch"
            >
              <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
            </button>
          </header>

          <div class="menu-search__search" dir="rtl">
            <div class="menu-search__search-box">
              <v-icon
                size="22"
                color="#78909C"
                class="menu-search__search-icon"
              >
                mdi-magnify
              </v-icon>
              <input
                ref="searchInput"
                v-model="search"
                type="search"
                class="menu-search__search-input"
                :placeholder="$t('search-in-menu')"
                autocomplete="off"
                @keydown.enter.prevent
              />
              <button
                v-if="search"
                type="button"
                class="menu-search__search-clear"
                :aria-label="$t('clear')"
                @click="clearSearch"
              >
                <v-icon size="18" color="#78909C">mdi-close-circle</v-icon>
              </button>
            </div>
          </div>

          <div class="menu-search__divider" />

          <div class="menu-search__body" dir="rtl">
            <button
              v-for="(item, index) in searchResults"
              :key="'sr-' + index"
              type="button"
              class="menu-search__item"
              @click="goToRoute(item)"
            >
              <span class="menu-search__item-icon">
                <v-icon size="22" color="#455A64">
                  {{ item.icon || "mdi-circle-outline" }}
                </v-icon>
              </span>
              <span class="menu-search__item-main">
                <span class="menu-search__item-title">{{ item.title }}</span>
                <span
                  v-if="item.parentTitle"
                  class="menu-search__item-sub"
                >
                  {{ item.parentTitle }}
                </span>
              </span>
              <v-icon size="18" color="#90A4AE">mdi-chevron-left</v-icon>
            </button>

            <div
              v-if="search && !searchResults.length"
              class="menu-search__empty"
            >
              {{ $t("search-in-menu") }}
            </div>
            <div
              v-else-if="!search"
              class="menu-search__empty"
            >
              {{ $t("search-in-menu") }}
            </div>
          </div>
        </v-card>
      </v-dialog>
    </v-card>
  </v-dialog>
</template>

<script>
import UserHelp from "@/views/dashboard/components/core/appbar/partial/UserHelp.vue";
// Redesigned notification center (UserHistory.vue kept untouched)
import UserHistory from "@/views/dashboard/components/core/appbar/partial/NotificationCenter.vue";
import UserSupport from "@/views/dashboard/components/core/appbar/partial/UserSupport.vue";
import MyIcon from "@/views/dashboard/components/general/icon/MyIcon.vue";

export default {
  name: "MenuHubMobile",
  components: {
    UserHelp,
    UserHistory,
    UserSupport,
    MyIcon,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      search: "",
      searchOpen: false,
      activeCategory: null,
    };
  },
  computed: {
    open: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    hubTitle() {
      return "منوی اصلی";
    },
    menuItem() {
      const menu = this.$store.state.menu;
      const topMenu = this.$store.state.topMenu || [];
      if (!menu || !Array.isArray(menu)) {
        return topMenu.length ? [...topMenu] : [];
      }
      if (topMenu.length > 0) {
        return [...topMenu, ...menu];
      }
      return menu;
    },
    computedItems() {
      return (this.menuItem || []).map((item) => this.mapItem(item));
    },
    visibleCategories() {
      return this.computedItems.filter((item) => this.resolveShowMenu(item));
    },
    visibleChildren() {
      if (!this.activeCategory || !this.activeCategory.children) return [];
      return this.activeCategory.children.filter(
        (child) =>
          child && child.show && child.to !== "divider" && child.to
      );
    },
    searchResults() {
      const q = (this.search || "").trim().toLowerCase();
      if (!q) return [];
      const results = [];
      this.computedItems.forEach((menu) => {
        if (!this.resolveShowMenu(menu)) return;
        if (menu.children && menu.children.length) {
          menu.children.forEach((child) => {
            if (
              child &&
              child.show &&
              child.to &&
              child.to !== "divider" &&
              String(child.title).toLowerCase().includes(q)
            ) {
              results.push({
                ...child,
                icon: child.icon || menu.icon,
                parentTitle: menu.title,
              });
            }
          });
        } else if (
          menu.to &&
          String(menu.title).toLowerCase().includes(q)
        ) {
          results.push(menu);
        }
      });
      return results;
    },
  },
  watch: {
    open(val) {
      if (val) {
        this.search = "";
        this.searchOpen = false;
        this.activeCategory = null;
        this.ensureMenuLoaded();
      } else {
        this.searchOpen = false;
        this.search = "";
      }
    },
    searchOpen(val) {
      if (val) {
        this.$nextTick(() => {
          const el = this.$refs.searchInput;
          if (el && typeof el.focus === "function") {
            setTimeout(() => el.focus(), 220);
          }
        });
      } else {
        this.search = "";
      }
    },
  },
  methods: {
    openSearch() {
      this.search = "";
      this.searchOpen = true;
    },
    closeSearch() {
      this.searchOpen = false;
      this.search = "";
    },
    clearSearch() {
      this.search = "";
      this.$nextTick(() => {
        const el = this.$refs.searchInput;
        if (el && typeof el.focus === "function") el.focus();
      });
    },
    triggerAction(key) {
      // Always close hub first so dialogs/routes are not trapped under fullscreen overlay
      this.open = false;

      if (key === "fileProcess") {
        this.$nextTick(() => {
          if (this.$root) {
            this.$root.$emit("open-user-file-process");
          }
        });
        return;
      }

      const comp = this.$refs[key];
      if (!comp) return;

      this.$nextTick(() => {
        if (key === "history" && typeof comp.openDialogButton === "function") {
          comp.openDialogButton();
          return;
        }
        if (key === "help" && typeof comp.openLink === "function") {
          comp.openLink(
            "https://abriron.com/%d8%a2%d9%85%d9%88%d8%b2%d8%b4-%d9%86%d8%b1%d9%85-%d8%a7%d9%81%d8%b2%d8%a7%d8%b1-%d8%ad%d8%b3%d8%a7%d8%a8%d8%af%d8%a7%d8%b1%db%8c-%d8%a2%d9%86%d9%84%d8%a7%db%8c%d9%86/"
          );
          return;
        }
        if (key === "support" && typeof comp.goTo === "function") {
          comp.goTo("userSupport");
          return;
        }
        const btn = comp.$el && comp.$el.querySelector(".v-btn");
        if (btn) btn.click();
      });
    },
    onBack() {
      if (this.searchOpen) {
        this.closeSearch();
        return;
      }
      if (this.activeCategory) {
        this.activeCategory = null;
        return;
      }
      this.open = false;
    },
    ensureMenuLoaded() {
      const menu = this.$store.state.menu;
      if (menu && Array.isArray(menu) && menu.length) return;
      this.$axios
        .get(this.$endpoint + "getdetail", {
          params: { select: "getPermissions" },
        })
        .then((res) => {
          res = res.data;
          this.$store.commit("SET_PERMISSIONS", res.permissions);
          this.$store.commit("SET_MENU", res.mn);
          this.$store.commit("SET_TOPMENU", res.tmn);
          if (res.config) {
            this.$store.commit(
              "SET_STUDENTLABEL",
              res.config.studentLabel
                ? res.config.studentLabel
                : this.$t("studentLabel")
            );
          }
        })
        .catch(() => {});
    },
    mapItem(item) {
      return {
        ...item,
        children: item.children ? item.children.map(this.mapItem) : undefined,
        title: this.$t(item.title, { name: this.$store.state.studentLabel }),
      };
    },
    resolveShowMenu(item) {
      if (item !== undefined && item.children !== undefined) {
        return item.children.some((child) => child.show === true);
      }
      if (item.children === undefined) {
        return true;
      }
      return false;
    },
    openCategory(item) {
      if (item.children && item.children.length) {
        this.activeCategory = item;
        return;
      }
      this.goToRoute(item);
    },
    goToRoute(item) {
      if (!item || !item.to) return;
      this.searchOpen = false;
      this.open = false;
      this.$nextTick(() => {
        this.$router
          .push({ name: item.to, params: item.params || {} })
          .catch(() => {});
      });
    },
  },
};
</script>

<style scoped>
.menu-hub {
  display: flex;
  flex-direction: column;
  height: 100vh;
  max-height: 100%;
  overflow: hidden;
  background: #f7f9fc;
}

.menu-hub__top {
  flex: 0 0 auto;
  background: #f7f9fc;
  z-index: 2;
}

.menu-hub__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  padding: 10px 12px;
  background: #fff;
  border-bottom: 1px solid #eef2f6;
}

.menu-hub__header-title {
  flex: 1 1 auto;
  font-size: 16px;
  font-weight: 700;
  color: #1c2a3a;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 4px;
}

.menu-hub__icon-btn {
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  padding: 0;
  border-radius: 8px;
  cursor: pointer;
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;
}

.menu-hub__icon-btn:active {
  background: rgba(69, 90, 100, 0.08);
}

.menu-hub__actions {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
  padding: 10px 12px 8px;
  background: #f7f9fc;
  border-bottom: 1px solid #eef2f6;
}

.menu-hub__action {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 68px;
  padding: 8px 6px 6px;
  border: none;
  border-radius: 12px;
  background: rgba(91, 124, 153, 0.08);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: transform 0.15s ease, background 0.15s ease;
}

.menu-hub__action:active {
  transform: scale(0.97);
  background: rgba(91, 124, 153, 0.16);
}

.menu-hub__action-icon {
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  /* clicks must hit the card so hub can close before navigate/dialog */
  pointer-events: none;
}

.menu-hub__action-icon ::v-deep .v-container {
  padding: 0 !important;
  margin: 0 !important;
}

.menu-hub__action-icon ::v-deep .v-btn {
  width: 36px !important;
  height: 36px !important;
  margin: 0;
  color: #5b7c99 !important;
}

.menu-hub__action-icon ::v-deep svg {
  width: 22px !important;
  height: 22px !important;
}

/* same blue as AppBar menu icon */
.menu-hub__action-icon ::v-deep svg [fill]:not([fill="none"]):not([fill="transparent"]) {
  fill: #5b7c99 !important;
}

.menu-hub__action-icon ::v-deep svg [stroke]:not([stroke="none"]) {
  stroke: #5b7c99 !important;
}

.menu-hub__action-icon ::v-deep .v-badge {
  margin: 0;
}

/* only history: expand red badge to number width */
.menu-hub__action--history ::v-deep .v-badge__badge {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  font-size: 9px !important;
  height: auto !important;
  min-height: 14px !important;
  min-width: unset !important;
  width: max-content !important;
  max-width: none !important;
  padding: 1px 6px !important;
  line-height: 1.1 !important;
  white-space: nowrap !important;
  border-radius: 999px !important;
  border: 1.5px solid #f7f9fc !important;
  box-sizing: content-box !important;
}

.menu-hub__action-label {
  font-size: 11px;
  font-weight: 600;
  color: #5b7c99;
  text-align: center;
  line-height: 1.2;
}

.menu-hub__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  padding: 10px 12px 16px;
}

.menu-hub__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.menu-hub__tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 96px;
  padding: 12px 8px;
  border: 1px solid #e6ebf1;
  border-radius: 14px;
  background: #fff;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.menu-hub__tile:active {
  background: #eef2f6;
}

.menu-hub__tile-label {
  font-size: 12px;
  font-weight: 600;
  color: #37474f;
  text-align: center;
  line-height: 1.3;
}

/* —— full-page search —— */
.menu-search {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f7f9fc;
}

.menu-search__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background: #fff;
  border-bottom: 1px solid #eef2f6;
  flex-shrink: 0;
}

.menu-search__title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #1c2a3a;
  text-align: center;
}

.menu-search__header-spacer {
  width: 36px;
  height: 36px;
}

.menu-search__search {
  flex: 0 0 auto;
  padding: 12px;
  background: #fff;
}

.menu-search__search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 48px;
  padding: 0 12px;
  border-radius: 10px;
  background: #eef2f6;
  border: 1px solid #e0e4e8;
  box-sizing: border-box;
}

.menu-search__search-icon {
  flex: 0 0 auto;
}

.menu-search__search-input {
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  border: 0;
  outline: none;
  background: transparent;
  font-size: 15px;
  font-weight: 500;
  color: #263238;
  text-align: right;
  line-height: 1.4;
}

.menu-search__search-input::placeholder {
  color: #90a4ae;
  font-weight: 400;
}

.menu-search__search-input::-webkit-search-cancel-button {
  display: none;
}

.menu-search__search-clear {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
}

.menu-search__divider {
  flex: 0 0 auto;
  height: 1px;
  background: #eceff1;
}

.menu-search__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 10px 12px 20px;
}

.menu-search__item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 52px;
  margin: 0 0 8px;
  padding: 10px 12px;
  border: 1px solid #e8eef3;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  text-align: right;
  -webkit-tap-highlight-color: transparent;
}

.menu-search__item:active {
  background: #eef2f6;
}

.menu-search__item-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #eef2f6;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.menu-search__item-main {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.menu-search__item-title {
  font-size: 14px;
  font-weight: 600;
  color: #37474f;
}

.menu-search__item-sub {
  font-size: 11px;
  color: #90a4ae;
}

.menu-search__empty {
  text-align: center;
  color: #90a4ae;
  font-size: 13px;
  padding: 40px 8px;
}
</style>
